import { HttpErrorResponse } from '@angular/common/http';
import { Component, DoCheck, OnDestroy, OnInit, inject, input, viewChild, output } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { BehaviorSubject, Observable, Subject, Subscription, catchError, defaultIfEmpty, of, switchMap } from 'rxjs';

import {
  AggFunc,
  AlertComponent,
  BaseComponent,
  BaseEditFormComponent,
  BaseInlineFormComponent,
  FormService,
  GridService,
  Guid,
  IdDescription,
  ignoreModalConfirm,
  ignoreModalError,
  ModalService,
  NotificationService,
  OkModalComponent,
  PrintService,
  ShipperResponse,
  TabGridComponent,
  YesNoModalComponent,
} from 'tms-lib';
import {
  AddressAddresseeReferenceService,
  AddressDetails,
  AddressService,
  CustomerAddress,
  DeliveryTermReferenceService,
  SalesDespatchNoteService,
  ShipperAccountService,
  ShipperChangeService,
  ShipperInterfaceDetailsService,
  ShipperInterfaceHeaderService,
  ShipperSupplier,
  ShippingPaymentRouteReferenceService,
  ShippingServiceService,
  Supplier,
  SupplierAddress,
  SupplierService,
  ThirdPartyShipperAccountReferenceService,
  ThirdPartyShipperAccountService,
  TmsFormlyAddressSearchService,
} from 'tms-web-shared';
import {
  ShipperAccount,
  ShipperBookingValidation,
  ShipperInterfaceDetails,
  ShipperInterfaceHeader,
  ShippingService,
  ThirdPartyShipperAccount,
} from '../../../models';
import { ShipperInterfaceDetailsModalComponent } from '../shipper-interface-details-modal/shipper-interface-details-modal.component';
import { AddressComponent } from '../../address/address/address.component';
import { AddressCreateAddresseesModalComponent } from '../../address/address-create-addressees-modal/address-create-addressees-modal.component';

@Component({
  selector: 'app-shipper-interface',
  templateUrl: './shipper-interface.component.html',
  styleUrl: './shipper-interface.component.scss',
  imports: [AlertComponent, BaseEditFormComponent, BaseInlineFormComponent, TabGridComponent],
})
export class ShipperInterfaceComponent extends BaseComponent implements OnInit, DoCheck, OnDestroy {
  protected readonly shipperInterfaceHeaderService = inject(ShipperInterfaceHeaderService);
  private readonly shipperInterfaceDetailsService = inject(ShipperInterfaceDetailsService);
  private readonly shipperChangeService = inject(ShipperChangeService);
  private readonly salesDespatchNoteService = inject(SalesDespatchNoteService);
  private readonly supplierService = inject(SupplierService);
  private readonly shippingServiceService = inject(ShippingServiceService);
  private readonly formService = inject(FormService);
  private readonly formlyService = inject(TmsFormlyAddressSearchService);
  private readonly gridService = inject(GridService);
  private readonly modalService = inject(ModalService);
  private readonly printService = inject(PrintService);
  private readonly notificationService = inject(NotificationService);
  private readonly addressService = inject(AddressService);
  private readonly addressAddresseeReferenceService = inject(AddressAddresseeReferenceService);
  private readonly deliveryTermService = inject(DeliveryTermReferenceService);
  private readonly shipperAccountService = inject(ShipperAccountService);
  private readonly shippingPaymentRouteReferenceService = inject(ShippingPaymentRouteReferenceService);
  private readonly thirdPartyShipperAccountReferenceService = inject(ThirdPartyShipperAccountReferenceService);
  private readonly thirdPartyShipperAccountService = inject(ThirdPartyShipperAccountService);

  private readonly form = viewChild(BaseEditFormComponent);
  private readonly grid = viewChild(TabGridComponent);

  public readonly salesDespatchNoteId = input<string>();
  public readonly salesManifestId = input<string>();
  public readonly goodsDeliveryNoteId = input<string>();
  public readonly shipperInterfaceHeaderId = input<string>();
  public readonly limitShippers = input(false);
  // Off in the quick despatch Shipping Details window, whose own Save books the shipping - a
  // second Book Shipping button there would book it twice.
  public readonly showButtons = input(true);
  public readonly supplierChanged = output<Supplier>();

  protected fields: FormlyFieldConfig[];
  protected buttonFields: FormlyFieldConfig[];
  protected show = true;

  // The booking checks (PRD pongees-points-oct-2026, R-5/R-6, D-7/D-8). They run on the SAVED record:
  // on open, after a Save (which reloads the model) and after the shipment details change. Book Shipping
  // stays disabled while they report anything, while they run, and while the form has unsaved edits.
  protected checkMessages: string[] = [];
  protected checking = true;
  protected unsavedChanges = false;
  // The button row's model - replaced (never mutated) to make Formly re-evaluate the Book Shipping
  // disabled expression, which reads component state and so is not re-checked on its own (ui.md).
  protected buttonModel: object = {};
  private bookShippingDisabled = true;
  private readonly checkBooking$ = new Subject<string>();

  private shippingServices$ = new BehaviorSubject<ShippingService[]>([]);
  private deliveryAddressees$ = new BehaviorSubject<IdDescription[]>([]);
  private shipperAccounts$ = new BehaviorSubject<IdDescription[]>([]);
  private deliveryTerms$: Observable<IdDescription[]>;
  private shippingPaymentRoutes$: Observable<IdDescription[]>;
  private thirdPartyShipperAccounts$: Observable<IdDescription[]>;
  private modalLevel: number;
  private saveSubscription: Subscription;
  private shipperSupplierId: string;

  protected columnDefs: ColDef[] = [
    this.gridService.text('shippingPackKey', $localize`Shipping Pack`, { aggFunc: AggFunc.count }),
    this.gridService.text('shippingPackDescription', $localize`Description`),
    this.gridService.numberFormat('grossWeight', $localize`Gross Weight`, {
      aggFunc: AggFunc.sum,
    }),
    this.gridService.numberFormat('length', $localize`Length`),
    this.gridService.numberFormat('width', $localize`Width`),
    this.gridService.numberFormat('height', $localize`Height`),
  ];

  ngOnInit(): void {
    this.getReferenceData();
    this.getFields();

    this.modalLevel = this.modalService.modalLevel;
    this.salesDespatchNoteService.refreshDetails.pipe(this.untilDestroyed()).subscribe(() => this.form().getModel());
    this.shipperInterfaceHeaderService.refreshDetails
      .pipe(this.untilDestroyed())
      .subscribe(() => this.form().getModel());

    // switchMap: a Save fires two reloads (updateModelOnSave and refreshDetails), and only the latest
    // answer may decide the button. A failed check leaves the button to the BookShipping backstop.
    this.checkBooking$
      .pipe(
        switchMap((id: string) =>
          this.shipperInterfaceHeaderService.validateBooking(id).pipe(
            catchError(() => of(undefined as ShipperBookingValidation)),
            defaultIfEmpty(undefined as ShipperBookingValidation),
          ),
        ),
        this.untilDestroyed(),
      )
      .subscribe((validation: ShipperBookingValidation) => {
        this.checkMessages = validation?.messages ?? [];
        this.checking = false;
      });
  }

  ngDoCheck(): void {
    // Typing in the edit form raises no event this component can hear, so the unsaved-edits state is
    // read on each check. Formly is nudged only when the button's state actually changes.
    const unsavedChanges = this.form()?.isDirty ?? false;
    const bookShippingDisabled = unsavedChanges || this.checking || this.checkMessages.length > 0;
    if (unsavedChanges !== this.unsavedChanges || bookShippingDisabled !== this.bookShippingDisabled) {
      this.unsavedChanges = unsavedChanges;
      this.bookShippingDisabled = bookShippingDisabled;
      this.buttonModel = { ...this.buttonModel };
    }
  }

  private checkBooking(shipperInterfaceHeaderId: string) {
    // No button row (the quick despatch Shipping Details window) means no Book Shipping to gate:
    // that window runs its own check when it saves.
    if (!this.showButtons() || !shipperInterfaceHeaderId || shipperInterfaceHeaderId === Guid.empty) {
      // Nothing saved to check - leave the button to the BookShipping backstop.
      this.checkMessages = [];
      this.checking = false;
      return;
    }

    this.checking = true;
    this.checkBooking$.next(shipperInterfaceHeaderId);
  }

  getReferenceData() {
    this.deliveryTerms$ = this.deliveryTermService.get();
    this.shippingPaymentRoutes$ = this.shippingPaymentRouteReferenceService.get();
    this.thirdPartyShipperAccounts$ = this.thirdPartyShipperAccountReferenceService.get();
  }

  ngOnDestroy() {
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }

  get model(): ShipperInterfaceHeader {
    return this.form()?.model();
  }

  modelLoaded(model: ShipperInterfaceHeader) {
    this.show = !!model.interfaceNumber;
    this.shipperSupplierId = model.supplierId;
    this.getShippingServices(this.shipperSupplierId);
    this.getShipperAccount(this.shipperSupplierId);
    this.getDeliveryAddressees(model.deliveryAddressId);
    if (this.show) {
      this.checkBooking(model.id);
    }

    if (model.thirdPartyId && model.thirdPartyAccountNo && !model.thirdPartyShipperAccount) {
      this.form().updateModel({ thirdPartyShipperAccount: model.thirdPartyAccountNo }, false);
    }

    this.form().updateModel(this.trackingFields(model.awbNumber, model.trackingNumber), false);
  }

  /** The carrier's numbers as the form shows them (point 7): the AWB as Tracking Number, else the one number. */
  private trackingFields(awbNumber: string, trackingNumber: string): Partial<ShipperInterfaceHeader> {
    return { awbNumber, trackingNumber, trackingNumberDisplay: awbNumber || trackingNumber };
  }

  /**
   * Booked with the carrier: it has a tracking number or an AWB (point 2). A manifest's shipment is the
   * exception - a DHL BBX manifest gets a piece id when it is created, long before it is sent - so on a
   * manifest only the AWB or the upload counts. The same rule as the API's ShipperChangeRules.IsBooked.
   */
  private isBooked(model: ShipperInterfaceHeader): boolean {
    if (model?.salesManifestId && model.salesManifestId !== Guid.empty) {
      return !!(model.awbNumber || model.salesManifestUploaded);
    }
    return !!(model?.awbNumber || model?.trackingNumber);
  }

  // A refused Save comes back as a 400 whose body is the (translated) reason - e.g. a shipper changed on a
  // booked shipment (AC-10). Shown as sent; anything else gets the usual save-failed message.
  protected saveFailed(error: HttpErrorResponse) {
    this.notificationService.apiFail(error, $localize`${this.shipperInterfaceHeaderService.name} save failed`);
  }

  saved() {
    this.shipperInterfaceHeaderService.refreshDetails.next();
  }

  private getFields() {
    this.fields = [
      ...this.formlyService.threeColumnGrid(
        [
          this.formlyService.textReadonly('interfaceNumber', $localize`Interface Number`),
          this.formlyService.date('dateShipped', $localize`Date Shipped`),
          ...this.formlyService.supplierShipperSearch(
            'supplierSearch',
            'supplierId',
            undefined,
            this.limitShippers() ? (model: ShipperInterfaceHeader) => model.sellingCompanyId : undefined,
            this.limitShippers() ? (model: ShipperInterfaceHeader) => model.warehouseId : undefined,
            (model: Supplier) => this.changeSupplier(model),
            () => this.clearSupplier(),
          ),
          this.formlyService.textReadonly('supplierKey', $localize`Shipper`, { required: true }),
          this.formlyService.textReadonly('supplierName', $localize`Name`, { required: true }),
          this.formlyService.select('shipperAccountId', $localize`Shipper Account`, this.shipperAccounts$, {
            required: true,
          }),
          this.formlyService.select('shippingServiceId', $localize`Shipping Service`, this.shippingServices$, {
            change: (field: FormlyFieldConfig, event) => {
              this.changeShippingService(event?.value);
              this.chooseShipperAccount(event?.value);
            },
            required: true,
          }),
          this.formlyService.textReadonly('shippingServiceKey', $localize`Shipping Key`, { required: true }),
          this.formlyService.numberReadonly('shippingCharge', $localize`Shipping Charge`, undefined, undefined, {
            'props.append': (field: FormlyFieldConfig) => field.model?.shipperCostCurrency,
          }),
          this.formlyService.select('billToId', $localize`Bill Shipping To`, this.shippingPaymentRoutes$),
          this.formlyService.select('dutyToId', $localize`Bill Duty To`, this.shippingPaymentRoutes$),
          this.formlyService.select('thirdPartyId', $localize`Third Party`, this.thirdPartyShipperAccounts$, {
            change: (field: FormlyFieldConfig, event) => this.changeThirdParty(event?.value),
          }),
          this.formlyService.text('thirdPartyShipperAccount', $localize`Third Party Shipper Account`),
          this.formlyService.text('reasonForSale', $localize`Reason for Sale`),
          this.formlyService.text('shipmentDescription', $localize`Shipment Description`),
        ],
        [
          ...this.formlyService.customerAddressSearch(
            'deliveryAddressSearchCustomer',
            'deliveryAddressMultiLine',
            $localize`Delivery Address`,
            (model: ShipperInterfaceHeader) => model.salesDespatchNoteCustomerId,
            (model: CustomerAddress) => this.changeDeliveryAddress(model),
            () => this.clearDeliveryAddress(),
            {
              editButton: true,
              onClick: () => this.editAddress(this.model.deliveryAddressId),
            },
            (field: FormlyFieldConfig) => field.model.goodsDeliveryNoteId || field.model.salesManifestId,
          ),
          // A manifest offers its shipper's addresses as well as the customer's (AC-33).
          ...this.formlyService.manifestDeliveryAddressSearch(
            'deliveryAddressSearchManifest',
            'deliveryAddressMultiLine',
            $localize`Delivery Address`,
            (model: ShipperInterfaceHeader) => model.salesDespatchNoteCustomerId,
            (model: ShipperInterfaceHeader) => model.supplierId,
            (model: CustomerAddress | SupplierAddress) => this.changeDeliveryAddress(model),
            () => this.clearDeliveryAddress(),
            {
              editButton: true,
              onClick: () => this.editAddress(this.model.deliveryAddressId),
            },
            (field: FormlyFieldConfig) => !field.model.salesManifestId,
          ),
          ...this.formlyService.supplierAddressSearch(
            'deliveryAddressSearchSupplier',
            'deliveryAddressMultiLine',
            $localize`Delivery Address`,
            (model: ShipperInterfaceHeader) => model.goodsDeliveryNoteSupplierId,
            (model: SupplierAddress) => this.changeDeliveryAddress(model),
            () => this.clearDeliveryAddress(),
            {
              editButton: true,
              onClick: () => this.editAddress(this.model.deliveryAddressId),
            },
            (field: FormlyFieldConfig) => !field.model.goodsDeliveryNoteId,
          ),
          this.formlyService.select('deliveryAddresseeId', $localize`Delivery Addressee`, this.deliveryAddressees$, {
            create: (event: any) => this.addAddressee(this.model.deliveryAddressId, event),
          }),
          this.formlyService.text('deliveryEmail', $localize`Delivery Email`),
          this.formlyService.text('deliveryTel', $localize`Delivery Telephone`),
          this.formlyService.select('deliveryTermId', $localize`Delivery Term`, this.deliveryTerms$),
          this.formlyService.checkbox('invoiceRequired', $localize`Invoice Required`),
          this.formlyService.checkbox('signatureRequired', $localize`Signature Required`),
          this.formlyService.checkbox('paperless', $localize`Paperless`),
          this.formlyService.text('percentageOfSaleValue', $localize`Percentage of Sale Value`),
          this.formlyService.checkbox('commercialInvoiceAtCost', $localize`Commercial Invoice at Cost`),
        ],
        [
          this.formlyService.textReadonly('sellingCompanyName', $localize`Selling Company`),
          this.formlyService.textReadonly('userEmailAddress', $localize`Email Address`),
          this.formlyService.textReadonly('customerRef1', $localize`Order Reference`),
          this.formlyService.textReadonly('customerRef2', $localize`Line Reference`),
          this.formlyService.textReadonly('productType', $localize`Product`),
          // D-4: the AWB is the Tracking Number, and the long DHL piece id is shown beside it as Piece ID.
          // Carriers with no AWB (UPS, DX) keep their one number as the Tracking Number.
          this.formlyService.textReadonly('trackingNumberDisplay', $localize`Tracking Number`),
          this.formlyService.textReadonly(
            'trackingNumber',
            $localize`Piece ID`,
            undefined,
            (field: FormlyFieldConfig) => !field.model?.awbNumber,
          ),
          this.formlyService.textReadonly('shipperRef', $localize`Shipper Ref`),
          this.formlyService.textarea('shippingNotes', $localize`Shipping Notes`),
        ],
      ),
    ];

    this.buttonFields = [
      this.formlyService.button($localize`Book Shipping`, () => this.bookShipping(), undefined, undefined, {
        'props.disabled': () => this.bookShippingDisabled,
      }),
      this.formlyService.button('Print Invoice', () => this.reprintInvoice()),
      this.formlyService.button('Reprint Label', () => this.reprintLabel()),
    ];
  }

  // Point 2 (D-1): a booked shipment keeps its shipper, and a change that would leave packs of the old
  // shipper asks first. Nothing in the form changes until it may, so No (or a refusal) leaves the old
  // shipper in place. The packs are deleted by the Save, which drops every pack of another shipper.
  private changeSupplier(model: Supplier) {
    const current = this.model;
    if (!current?.supplierId || current.supplierId === model.id) {
      this.applySupplier(model);
      return;
    }

    if (this.isBooked(current)) {
      this.shipperChangeService.refuseBooked(current.supplierName);
      return;
    }

    const packCount = current.shipperInterfaceDetails?.length ?? 0;
    if (packCount === 0) {
      this.applySupplier(model);
      return;
    }

    this.shipperChangeService.confirmPackDelete(packCount, current.supplierName).then((yes: boolean) => {
      if (yes) {
        this.form().updateModel({ shipperInterfaceDetails: [] });
        this.applySupplier(model);
      }
    });
  }

  private applySupplier(model: Supplier) {
    this.supplierChanged.emit(model);
    this.supplierService
      .getSupplierForShipping(model.id, this.model.sellingCompanyId, this.model.shipperAccountWarehouseId)
      .pipe(this.untilDestroyed())
      .subscribe((shipperSupplier: ShipperSupplier) => {
        this.form().updateModel(shipperSupplier);
        this.shipperSupplierId = shipperSupplier.supplierId;
        this.getShippingServices(shipperSupplier.supplierId);
        // The account drop-down offers the new shipper's accounts (AC-9).
        this.getShipperAccount(shipperSupplier.supplierId);
      });
  }

  // The account follows the service: a consolidated service takes the consolidated account (AC-5).
  // Only when the current account is the wrong kind for the new service - a shipper can hold dozens of
  // accounts of one kind (DHL EXPRESS: one per brand), and the one the user chose must not be replaced.
  private chooseShipperAccount(shippingServiceId: string) {
    if (!shippingServiceId || !this.model?.supplierId) {
      return;
    }

    if (this.currentAccountSuitsService(shippingServiceId)) {
      return;
    }

    this.supplierService
      .getSupplierForShipping(
        this.model.supplierId,
        this.model.sellingCompanyId,
        this.model.shipperAccountWarehouseId,
        shippingServiceId,
      )
      .pipe(this.untilDestroyed())
      .subscribe((shipperSupplier: ShipperSupplier) => {
        if (shipperSupplier?.shipperAccountId) {
          this.form().updateModel({
            shipperAccountId: shipperSupplier.shipperAccountId,
            shipperAccountKey: shipperSupplier.shipperAccountKey,
          });
        }
      });
  }

  /** The current account is one of the shipper's and its consolidated flag matches the service's. */
  private currentAccountSuitsService(shippingServiceId: string): boolean {
    const shippingService = this.shippingServices$.value.find((x) => x.id === shippingServiceId);
    const currentAccount = (this.shipperAccounts$.value as ShipperAccount[]).find(
      (x) => x.id === this.model?.shipperAccountId,
    );
    return (
      !!shippingService && !!currentAccount && !!currentAccount.consolidatedAccount === !!shippingService.consolidated
    );
  }

  private clearSupplier() {
    this.form().updateModel({
      supplierId: undefined,
      supplierKey: undefined,
      supplierName: undefined,
      shipperAccountKey: undefined,
      shippingServiceId: undefined,
    });
    this.changeShippingService(undefined);
  }

  private getShippingServices(supplierId: string) {
    this.shippingServiceService
      .getForSupplier(supplierId)
      .pipe(this.untilDestroyed())
      .subscribe((shippingServices: ShippingService[]) => {
        this.shippingServices$.next(shippingServices);
        if (
          shippingServices.every(
            (shippingService: ShippingService) => shippingService.id !== this.model.shippingServiceId,
          )
        ) {
          this.form().updateModel({ shippingServiceId: undefined }, false);
          // An automatic clear on load, not a user edit - so it must not mark the form changed, which
          // would hold Book Shipping disabled on a record nobody touched.
          this.changeShippingService(undefined, false);
        }
      });
  }

  private getShipperAccount(supplierId: string) {
    this.shipperAccountService
      .getForSupplier(supplierId)
      .pipe(this.untilDestroyed())
      .subscribe((shipperAccounts: ShipperAccount[]) => {
        this.shipperAccounts$.next(shipperAccounts);
        if (
          shipperAccounts.every((shipperAccount: ShipperAccount) => shipperAccount.id !== this.model.shipperAccountId)
        ) {
          this.form().updateModel({ shipperAccountId: undefined }, false);
        }
      });
  }

  private changeShippingService(shippingServiceId: string, markUpdated = true) {
    const shippingService = this.shippingServices$.value.find((x) => x.id === shippingServiceId);
    this.form().updateModel(
      {
        shippingServiceKey: shippingService?.key,
        shippingCharge: shippingService?.shippingCost,
      },
      markUpdated,
    );
  }

  private changeThirdParty(thirdPartyId: string) {
    if (!thirdPartyId) {
      this.form().updateModel({ thirdPartyShipperAccount: undefined });
      return;
    }

    this.thirdPartyShipperAccountService
      .edit(thirdPartyId)
      .pipe(this.untilDestroyed())
      .subscribe((account: ThirdPartyShipperAccount) => {
        this.form().updateModel({ thirdPartyShipperAccount: account?.accountReference });
      });
  }

  private bookShipping() {
    if (this.formService.anyFormChanges(this.modalLevel)) {
      const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
      component.header.set($localize`Save Changes`);
      component.message.set($localize`OK to save changes before booking shipping for this delivery?`);
      modal.result.then((yes: boolean) => {
        if (yes) {
          this.saveSubscription = this.formService.saveComplete.pipe(this.untilDestroyed()).subscribe(() => {
            this.saveSubscription.unsubscribe();
            this.confirmBookShipping();
          });
          this.formService.save.next(this.modalLevel);
        }
      }, ignoreModalError);
    } else {
      this.confirmBookShipping();
    }
  }

  private confirmBookShipping() {
    const { modal, component } = this.modalService.openSmall<YesNoModalComponent>(YesNoModalComponent);
    component.header.set($localize`Book Shipping`);
    component.message.set($localize`OK to book shipping for this delivery?`);
    modal.result.then((yes: boolean) => {
      if (yes) {
        this.shipperInterfaceHeaderService
          .bookShipping(this.model.id, this.model.shippingNotes)
          .pipe(this.untilDestroyed())
          .subscribe((response: ShipperResponse) => {
            // Booked, but with something the user should know - e.g. a line sent to an EU consumer
            // without its product identifiers. Already translated by the API.
            (response.cautions ?? []).forEach((caution: string) => this.notificationService.warning(caution));
            if (response.trackingNumber) {
              // Straight onto the form, without waiting for the reload (AC-30).
              this.form().updateModel(this.trackingFields(response.awbNumber, response.trackingNumber), false);
              setTimeout(() => this.reprintLabel(), 0);
              this.shipperInterfaceHeaderService.refreshDetails.next();
            } else {
              const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
              component.header.set($localize`Book Shipping`);
              component.message.set($localize`Booking Failed` + ` - ${response.errorMessage}`);
              modal.result.then(ignoreModalConfirm, ignoreModalError);
            }
          });
      }
    }, ignoreModalError);
  }

  private reprintLabel() {
    this.shipperInterfaceHeaderService
      .reprintLabel(
        !this.salesDespatchNoteId() || this.salesDespatchNoteId() === Guid.empty
          ? this.goodsDeliveryNoteId()
          : this.salesDespatchNoteId(),
        this.model.id,
      )
      .pipe(this.untilDestroyed())
      .subscribe((response: ShipperResponse) => {
        if (response.trackingNumber) {
          this.form().updateModel(this.trackingFields(response.awbNumber, response.trackingNumber), false);
          this.printService.printShippingLabels(response).pipe(this.untilDestroyed()).subscribe();
          this.shipperInterfaceHeaderService.refreshDetails.next();
        } else {
          const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
          component.header.set($localize`Book Shipping`);
          component.message.set($localize`Booking Failed` + ` - ${response.errorMessage}`);
          modal.result.then(ignoreModalConfirm, ignoreModalError);
        }
      });
  }

  private reprintInvoice() {
    this.shipperInterfaceHeaderService
      .reprintLabel(
        !this.salesDespatchNoteId() || this.salesDespatchNoteId() === Guid.empty
          ? this.goodsDeliveryNoteId()
          : this.salesDespatchNoteId(),
        this.model.id,
      )
      .pipe(this.untilDestroyed())
      .subscribe((response: ShipperResponse) => {
        if (response.trackingNumber) {
          this.form().updateModel(this.trackingFields(response.awbNumber, response.trackingNumber), false);
          this.printService.printShippingInvoices(response).pipe(this.untilDestroyed()).subscribe();
          this.shipperInterfaceHeaderService.refreshDetails.next();
        }
      });
  }

  private changeDeliveryAddress(model: CustomerAddress | SupplierAddress) {
    this.addressService
      .edit(model.addressId)
      .pipe(this.untilDestroyed())
      .subscribe((address: AddressDetails) => this.updateDeliveryAddress(address));
  }

  private updateDeliveryAddressee(addressId: string, addresseeId: string) {
    this.form().updateModel({ deliveryAddresseeId: addresseeId });
    this.getDeliveryAddressees(addressId);
  }

  private clearDeliveryAddress() {
    this.form().updateModel({
      deliveryAddressId: undefined,
      deliveryAddressMultiLine: undefined,
      deliveryAddresseeId: undefined,
      deliveryEmail: undefined,
      deliveryTel: undefined,
    });
  }

  private getDeliveryAddressees(addressId: string) {
    this.addressAddresseeReferenceService
      .getForAddress(addressId)
      .pipe(this.untilDestroyed())
      .subscribe((deliveryAddressees: IdDescription[]) => {
        this.deliveryAddressees$.next(deliveryAddressees);
        if (
          !!this.model.deliveryAddresseeId &&
          deliveryAddressees.every(
            (deliveryAddressee: IdDescription) => deliveryAddressee.id !== this.model.deliveryAddresseeId,
          )
        ) {
          // Automatic clear on load - not a user edit (see getShippingServices).
          this.form().updateModel({ deliveryAddresseeId: undefined }, false);
        }
      });
  }

  private editAddress(addressId: string) {
    if (!addressId) return;

    this.addressService
      .edit(addressId)
      .pipe(this.untilDestroyed())
      .subscribe((address: AddressDetails) => {
        const { modal, component } = this.modalService.openLarge<AddressComponent>(AddressComponent);
        component.id.set(address.id);
        component.isModal.set(true);
        modal.result.then(
          () => {
            this.addressService
              .edit(address.id)
              .pipe(this.untilDestroyed())
              .subscribe((model: AddressDetails) => this.updateDeliveryAddress(model));
          },
          () => {
            this.addressService
              .edit(address.id)
              .pipe(this.untilDestroyed())
              .subscribe((model: AddressDetails) => this.updateDeliveryAddress(model));
          },
        );
      });
  }

  private updateDeliveryAddress(address: AddressDetails) {
    this.form().updateModel({
      deliveryAddressId: address.id,
      deliveryAddressMultiLine: address.multiLine,
      deliveryEmail: address.email,
      deliveryTel: address.phone,
    });
    this.getDeliveryAddressees(address.id);
  }

  private addAddressee(addressId: string, event: any) {
    if (!addressId || !event.target.nodeName) return;
    const { modal, component } = this.modalService.openMedium<AddressCreateAddresseesModalComponent>(
      AddressCreateAddresseesModalComponent,
    );
    component.id.set(undefined);
    component.addressId.set(addressId);
    modal.result.then(() => this.updateDeliveryAddressee(addressId, component.newAddressee.id), ignoreModalError);
  }

  create() {
    this.openShipperInterfaceDetails(undefined);
  }

  editDetails(model: ShipperInterfaceDetails) {
    this.openShipperInterfaceDetails(model);
  }

  delete(model: ShipperInterfaceDetails) {
    this.shipperInterfaceDetailsService
      .delete(model.id)
      .pipe(this.untilDestroyed())
      .subscribe(() => this.form().getModel());
  }

  private openShipperInterfaceDetails(model: ShipperInterfaceDetails) {
    const { modal, component } = this.modalService.openMedium<ShipperInterfaceDetailsModalComponent>(
      ShipperInterfaceDetailsModalComponent,
    );
    component.id.set(model?.id);
    component.shippingInterfaceHeaderId.set(this.shipperInterfaceHeaderId());
    component.shipperSupplierId.set(this.shipperSupplierId);
    modal.result.then(
      () =>
        this.shipperInterfaceDetailsService
          .search({ search: '', id: this.model.id })
          .pipe(this.untilDestroyed())
          .subscribe((details) => {
            this.form().updateModel(
              {
                shipperInterfaceDetails: details,
              },
              false,
            );
            // The packs are saved by the modal, so the checks (packs, pack type) can re-run now.
            this.checkBooking(this.model.id);
          }),
      ignoreModalError,
    );
  }
}
